/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Outlined input: the single control-height system for the app.
 *
 * Medium is 48px and small is 40px, and the theme only ever sets `minHeight`
 * on the root. The value element (input, textarea, or the Select's div) has no
 * vertical padding and stretches to fill the root, so a page that still pins a
 * larger `height` on the root keeps a centred value, and a page that pins a
 * smaller one is ignored exactly as it was before (min-height beats height).
 *
 * Autocomplete carries its own padding rules at higher specificity; those are
 * matched in form/autocomplete.js, not here.
 */

import colors from "assets/theme-dark/base/colors";
import borders from "assets/theme-dark/base/borders";
import typography from "assets/theme-dark/base/typography";
import pxToRem from "assets/theme-dark/functions/pxToRem";
import rgba from "assets/theme-dark/functions/rgba";

const { brand, outline, surface, text, transparent, error } = colors;
const { borderRadius } = borders;
const { fontFamily } = typography;

const inputOutlined = {
  styleOverrides: {
    root: {
      backgroundColor: transparent.main,
      fontFamily,
      fontSize: pxToRem(14),
      lineHeight: 1.4,
      color: text.main,
      borderRadius: borderRadius.md,
      minHeight: pxToRem(48),
      transition: "box-shadow 150ms ease, border-color 150ms ease",

      "&:hover:not(.Mui-focused):not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
        borderColor: outline.main,
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 3px ${rgba(brand.primary, 0.14)}`,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: brand.primary,
        borderWidth: 1,
      },
      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: error.main,
      },
      "&.Mui-error.Mui-focused": {
        boxShadow: `0 0 0 3px ${rgba(error.main, 0.12)}`,
      },
      "&.Mui-disabled": {
        backgroundColor: surface.main,
      },
      "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
        borderColor: outline.variant,
      },

      // Every value element fills the root; the root's minHeight is the height.
      "& .MuiInputBase-input": {
        height: "auto",
        alignSelf: "stretch",
        boxSizing: "border-box",
      },
      "& .MuiSelect-select": {
        paddingLeft: pxToRem(14),
      },
    },

    notchedOutline: {
      borderColor: outline.variant,
      borderWidth: 1,
    },

    input: {
      padding: `0 ${pxToRem(14)}`,
      color: text.main,
      backgroundColor: transparent.main,
      "&::placeholder": {
        color: text.focus,
        opacity: 1,
      },
    },
    inputAdornedStart: { paddingLeft: 0 },
    inputAdornedEnd: { paddingRight: 0 },
    adornedStart: { paddingLeft: pxToRem(14) },
    adornedEnd: { paddingRight: pxToRem(14) },

    multiline: {
      padding: `${pxToRem(12)} ${pxToRem(14)}`,
      alignItems: "flex-start",
    },
    inputMultiline: {
      padding: 0,
      alignSelf: "auto",
      height: "auto",
    },

    sizeSmall: {
      minHeight: pxToRem(40),
      fontSize: pxToRem(13),
      borderRadius: borderRadius.sm,
      "& .MuiSelect-select": {
        paddingLeft: pxToRem(12),
      },
      "& .MuiSelect-icon": {
        right: pxToRem(8),
      },
    },
    inputSizeSmall: {
      padding: `0 ${pxToRem(12)}`,
      fontSize: pxToRem(13),
    },
  },
};

export default inputOutlined;
